// May this piece be taken apart, and which ones may be.
//
// Lifted out of the reducer when the shelf needed to light the squares. The
// reducer had the whole rule inline and answered it one square at a time, so
// the only way for the interface to offer the power was to restate six
// conditions beside it — and a second copy of "may this be taken apart" is how
// a control comes to be drawn live over a square the reducer then refuses.
//
// **The reducer is still the authority.** This decides what to *draw*; every
// play goes back through `applyCommand`, which asks this same function. That
// is the split `canTakeVariant` documents, arrived at for the same reason.
//
// Types only from `commands.ts` — `GameState` is erased at compile time, so
// there is no runtime edge and no cycle. See the reducer's own note.

import { type Color, pieceColor, type PieceType } from "../board";
import { canBeTaken } from "../chess/moveGen";
import type { GameState } from "../commands";
import { altarBeside, neighbours, touches } from "../industry/structures";
import { ALTAR, type IndustryState, makeUnit, sacrificedAtOn, structureAt, structureSquares } from "../industry/state";
import { mintBill } from "../materials";
import { pieceAt } from "../position";
import { poweredBearers } from "./equipment";
import { hasPower } from "./powers";

/**
 * Why a sacrifice is refused.
 *
 * Each names the thing that is wrong, because a shared refusal is a control
 * that says "no" and leaves the player to guess which of six rules they broke.
 */
export type SacrificeError =
  | "not-your-turn"
  | "no-power"
  | "in-check"
  | "no-king"
  | "already-this-turn"
  | "not-your-piece"
  | "cannot-be-taken"
  | "not-beside-your-altar";

/**
 * Where a sacrifice or a capture bonus lands (§31 A3): an altar's stock, or a
 * Kris-bearing Monarch's inventory (its inbox). The design's "based on which
 * you used"; where both reach, the altar.
 */
export type Furnace = { kind: "altar"; at: number } | { kind: "kris"; at: number };

/**
 * The king this side would sacrifice *to*, or -1.
 *
 * Its own step because two of the conditions are about the king rather than
 * about the piece — where the metal goes, and whose turn-stamp is spent — so
 * `sacrificeTargets` needs it once rather than per candidate.
 */
function kingOf(state: GameState, owner: Color): number {
  const king = state.position.kings[owner];
  return king === undefined || king < 0 ? -1 : king;
}

/**
 * The furnace this sacrifice would land in, or null (§31 A3): an own altar
 * beside `at`, else a Kris bearer of `owner`'s beside it, first by gear list.
 *
 * Exported because the reducer needs the same answer the predicate gave —
 * two readings of "which furnace takes it" is a control lighting a square
 * the yield then leaves by a different door. The altar wins where a piece is
 * beside both. A bearer never reaches itself: it cannot be taken apart into
 * its own hands, and nothing stands on an altar — the doc's "on" is moot.
 */
export function sacrificeFurnace(state: GameState, owner: Color, at: number): Furnace | null {
  return furnaceBeside(state.industry, owner, at, -1);
}

/**
 * The furnace a victim at `at` feeds, for `owner`'s Sacrifice power: an
 * altar beside it, a Kris bearer beside it, or the bearer that is the
 * capturer (`by`, the capture bonus's "with the equipped Monarch").
 */
export function furnaceBeside(industry: IndustryState, owner: Color, at: number, by: number): Furnace | null {
  if (!hasPower(industry, owner, "sacrifice")) return null;
  const altar = altarBeside(industry, owner, at);
  if (altar >= 0) return { kind: "altar", at: altar };
  for (const bearer of poweredBearers(industry, owner, ALTAR)) {
    if (bearer === by || touches(bearer, at)) return { kind: "kris", at: bearer };
  }
  return null;
}

/**
 * What a sacrificed piece comes apart into (§31 A3): half of each row of its
 * forge bill, rounded up, at that row's grade and line — clean, no refinery
 * mask. Still a loss: half a bill is below the bill.
 */
export function sacrificeYield(type: PieceType): number[] {
  const out: number[] = [];
  for (const row of mintBill(type)) {
    for (let i = 0; i < Math.ceil(row.count / 2); i++) out.push(makeUnit(row.grade, 0, row.line));
  }
  return out;
}

/**
 * Everything that is true before any particular piece is named.
 *
 * Split out so the target sweep asks it once. The order is the reducer's, and
 * it is the cheap-to-answer, most-explanatory-first order a player would want:
 * "you do not have this" before "not while you are in check" before "you have
 * already done it".
 */
export function sacrificeBlocked(state: GameState, owner: Color): SacrificeError | null {
  // **Mirrors `requireOwnTurn`, and it has to.** Sacrifice spends the turn, so
  // the reducer refuses it off the clock — and without this the shelf lights
  // squares during the opponent's turn that the reducer then refuses, which is
  // exactly the disagreement this module exists to make impossible.
  if (state.position.turn !== owner) return "not-your-turn";
  // **The power: a standing altar** (§26 A1, F6). The army styles that had it
  // by `kingKind` are retired (§31 R2).
  if (!hasPower(state.industry, owner, "sacrifice")) return "no-power";
  // Taking a piece of your own off the board can never resolve a check — the
  // re-aim argument — so it is refused outright rather than tried and rolled
  // back.
  if (state.status === "check") return "in-check";
  const king = kingOf(state, owner);
  if (king < 0) return "no-king";
  // Once a turn, stamped on the king with the round — a side has one turn a
  // round, and `turnCounter` is ticks (§31 T). Not one of its three jobs:
  // sacrificing does not stop it mining and mining does not stop this.
  if (sacrificedAtOn(state.industry, king) === state.position.round) {
    return "already-this-turn";
  }
  return null;
}

/**
 * May this square's piece be taken apart right now?
 *
 * Answers about the board *and* the run's powers, unlike `canTakeVariant` — the
 * difference is that nothing searches over sacrifices, so there is no planner
 * that would have to be handed a deck to ask it.
 */
export function canSacrifice(
  state: GameState,
  owner: Color,
  at: number
): SacrificeError | null {
  const blocked = sacrificeBlocked(state, owner);
  if (blocked) return blocked;

  const piece = pieceAt(state.position, at);
  if (!piece || pieceColor(piece) !== owner) return "not-your-piece";
  // The one place that answers "may this be removed at all" — not a king, not
  // one under a Stasis Field. Shared with capture rather than restated: half
  // applying "a king cannot be removed" is how a game reaches a position with
  // no king in it.
  if (!canBeTaken(state.position, at, piece)) return "cannot-be-taken";
  // **Reach is an altar's eight neighbours, or a Kris bearer's** (§31 A3).
  if (!sacrificeFurnace(state, owner, at)) return "not-beside-your-altar";
  return null;
}

/**
 * Every piece this side could take apart this turn.
 *
 * Swept over the furnaces' own neighbours rather than over the board, because
 * that is the condition that actually bounds it — eight squares per furnace
 * against however many thousand a grown board has, asked every time the shelf
 * redraws.
 *
 * It does **not** ask `requireKingSafe`. A sacrifice can open a line onto your
 * own king and the reducer refuses that one after the fact, which needs a
 * cloned state per candidate; offering it and refusing on the click is the
 * cheaper honest trade, and the refusal says so.
 */
export function sacrificeTargets(state: GameState, owner: Color): number[] {
  if (sacrificeBlocked(state, owner)) return [];
  const candidates = new Set<number>();
  if (hasPower(state.industry, owner, "sacrifice")) {
    for (const sq of structureSquares(state.industry)) {
      const structure = structureAt(state.industry, sq)!;
      if (structure.kind !== ALTAR || structure.owner !== owner) continue;
      for (const at of neighbours(sq)) candidates.add(at);
    }
    for (const bearer of poweredBearers(state.industry, owner, ALTAR)) {
      for (const at of neighbours(bearer)) candidates.add(at);
    }
  }
  // Sorted, because a Set remembers insertion order and two altars can
  // reach one square — the board's highlights have to come out the same on
  // two clients whatever order the sweeps ran in.
  return [...candidates]
    .sort((a, b) => a - b)
    .filter((at) => canSacrifice(state, owner, at) === null);
}
